import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Building2,
  Plus,
  Users,
  UserPlus,
  Trash2,
  ChevronRight,
  ArrowLeft,
  Clock,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listCompanies,
  getCompany,
  createCompany,
  deleteCompany,
  inviteToCompany,
  revokeCompanyInvite,
  updateCompanyMember,
  updateCompany,
} from '@/lib/auth';
import { format, parseISO } from 'date-fns';
import { useT } from '@/lib/LanguageContext';
import ModulesCard, { TemplatePicker } from '@/components/companies/ModulesCard';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return format(typeof d === 'string' ? parseISO(d) : new Date(d), 'MMM d, yyyy'); } catch { return '—'; }
};

const roleBadge = (role) => {
  const s = { owner: 'bg-purple-100 text-purple-700', admin: 'bg-blue-100 text-blue-700', member: 'bg-slate-100 text-slate-600' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s[role] || s.member}`}>{role}</span>;
};

// ─── Company List View ────────────────────────────────────────────────────────

function CompanyList({ onSelect }) {
  const t = useT();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [template, setTemplate] = useState('core');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const company = await createCompany({ name: newName.trim(), template });
      toast.success(`Company "${company.name}" created`);
      setCreateOpen(false);
      setNewName('');
      setTemplate('core');
      await load();
    } catch (err) {
      toast.error('Failed to create company', { description: err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (company) => {
    try {
      await deleteCompany(company.id);
      toast.success('Company deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error('Cannot delete company', { description: err.message });
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('nav.companies')}
          </h1>
          <p className="text-muted-foreground">
            Create and manage client companies. Invite team members to each one.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#A62B1F] hover:bg-[#8C2318] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Company
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      ) : companies.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="w-10 h-10 mx-auto text-[#D3CCBE] mb-3" />
            <p className="font-semibold text-[#1E2A33]">No companies yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first company to get started.</p>
            <Button onClick={() => setCreateOpen(true)} className="mt-4 bg-[#A62B1F] hover:bg-[#8C2318] text-white">
              <Plus className="w-4 h-4 mr-2" /> New Company
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {companies.map((c) => (
            <Card
              key={c.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onSelect(c)}
            >
              <CardContent className="py-4 px-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-[#F8EFED] flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-[#1E2A33]" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[#1E2A33] truncate">{c.name}</p>
                      <Badge
                        variant="outline"
                        className={c.status === 'active' ? 'text-green-700 border-green-300 bg-green-50' : 'text-red-600 border-red-300 bg-red-50'}
                      >
                        {c.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#5C6670]">slug: {c.slug}{Array.isArray(c.modules) ? ` · ${c.modules.length} module${c.modules.length === 1 ? '' : 's'}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                  <div className="text-center hidden sm:block">
                    <p className="text-lg font-bold text-[#1E2A33]">{c.member_count}</p>
                    <p className="text-xs text-[#5C6670]">members</p>
                  </div>
                  {c.pending_invites > 0 && (
                    <div className="text-center hidden sm:block">
                      <p className="text-lg font-bold text-amber-600">{c.pending_invites}</p>
                      <p className="text-xs text-[#5C6670]">pending</p>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-400 hover:text-red-600"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                      title="Delete company"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-[#5C6670]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Company</DialogTitle>
            <DialogDescription>Create a new company workspace. You can invite members after.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="company-name">{t('common.company')}</Label>
              <Input
                id="company-name"
                placeholder="Acme Corp"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <Label>Start with</Label>
              <p className="text-xs text-[#5C6670] mb-2">Switches on a set of modules. You can change them any time.</p>
              <TemplatePicker value={template} onChange={setTemplate} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={creating} className="bg-[#A62B1F] hover:bg-[#8C2318] text-white">
                {creating ? t('common.loading') : 'Create Company'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete "{deleteTarget?.name}"?</DialogTitle>
            <DialogDescription>
              This cannot be undone. Companies with active members cannot be deleted — deactivate all members first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteTarget)}>{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Company Detail View ──────────────────────────────────────────────────────

function CompanyDetail({ companyId, companyName, onBack }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const result = await getCompany(companyId);
      setData(result);
    } catch (err) {
      toast.error('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [companyId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const result = await inviteToCompany(companyId, { email: inviteEmail.trim(), role: inviteRole });
      const link = `${window.location.origin}/invite/${result.token}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Invite created — link copied to clipboard!');
      } catch {
        toast.success('Invite created!', { description: `Link: ${link}` });
      }
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      await load();
    } catch (err) {
      toast.error('Failed to create invite', { description: err.message });
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (inviteId) => {
    try {
      await revokeCompanyInvite(companyId, inviteId);
      toast.success('Invite revoked');
      setData((prev) => prev ? { ...prev, invites: prev.invites.filter((i) => i.id !== inviteId) } : prev);
    } catch {
      toast.error('Failed to revoke invite');
    }
  };


  const handleToggleActive = async (member) => {
    try {
      await updateCompanyMember(companyId, member.id, { is_active: !member.is_active });
      setData((prev) => prev ? {
        ...prev,
        members: prev.members.map((m) => m.id === member.id ? { ...m, is_active: !m.is_active } : m)
      } : prev);
      toast.success(member.is_active ? 'Member deactivated' : 'Member reactivated');
    } catch {
      toast.error('Failed to update member');
    }
  };

  return (
    <>
      <div className="mb-8">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-[#5C6670] hover:text-[#1E2A33] mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Companies
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {companyName}
            </h1>
            <p className="text-muted-foreground text-sm">Manage members and invites for this company.</p>
          </div>
          <Button
            onClick={() => setInviteOpen(true)}
            className="bg-[#A62B1F] hover:bg-[#8C2318] text-white"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {t('settings.invite_member')}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      ) : !data ? null : (
        <div className="space-y-6">
          {/* Modules */}
          <ModulesCard companyId={companyId} data={data} onSaved={(r) => setData((prev) => (prev ? { ...prev, ...r } : prev))} />

          {/* Members */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#A62B1F]" />
                <div>
                  <CardTitle>Members</CardTitle>
                  <CardDescription>{data.members.filter((m) => m.is_active).length} active</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {data.members.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No members yet. Send an invite to get started.
                </p>
              ) : (
                <div className="divide-y divide-[#E3DDD1]">
                  {data.members.map((m) => (
                    <div key={m.id} className="py-3 flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#1E2A33] truncate">
                            {m.full_name || '(no name)'}
                          </p>
                          {roleBadge(m.role)}
                          {!m.is_active && <span className="text-xs text-red-500 font-medium">Deactivated</span>}
                        </div>
                        <p className="text-xs text-[#5C6670]">{m.email}</p>
                        <p className="text-xs text-[#9AA3AB]">Last login: {fmtDate(m.last_login)}</p>
                      </div>
                      {m.role !== 'owner' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className={m.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                          onClick={() => handleToggleActive(m)}
                        >
                          {m.is_active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Invites */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#A62B1F]" />
                <div>
                  <CardTitle>Pending Invites</CardTitle>
                  <CardDescription>{data.invites.length} awaiting acceptance</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {data.invites.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No pending invites.</p>
              ) : (
                <div className="divide-y divide-[#E3DDD1]">
                  {data.invites.map((inv) => (
                    <div key={inv.id} className="py-3 flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-[#5C6670]" />
                          <p className="text-sm font-medium text-[#1E2A33] truncate">{inv.email}</p>
                          {roleBadge(inv.role)}
                        </div>
                        <p className="text-xs text-[#5C6670]">Expires: {fmtDate(inv.expires_at)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-400 hover:text-red-600"
                        onClick={() => handleRevoke(inv.id)}
                        title="Revoke"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite to {companyName}</DialogTitle>
            <DialogDescription>
              They'll get a unique link to create their account. Links expire in 7 days.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="inv-email">{t('settings.invite_email')}</Label>
              <Input
                id="inv-email"
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member — standard access</SelectItem>
                  <SelectItem value="admin">Admin — can invite others &amp; manage settings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={inviting} className="bg-[#A62B1F] hover:bg-[#8C2318] text-white">
                {inviting ? t('common.loading') : 'Create Invite & Copy Link'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Companies() {
  const t = useT();
  const [selectedCompany, setSelectedCompany] = useState(null);

  return (
    <Layout title={t('nav.companies')}>
      <div className="container mx-auto p-8 max-w-4xl">
        {selectedCompany ? (
          <CompanyDetail
            companyId={selectedCompany.id}
            companyName={selectedCompany.name}
            onBack={() => setSelectedCompany(null)}
          />
        ) : (
          <CompanyList onSelect={setSelectedCompany} />
        )}
      </div>
    </Layout>
  );
}
